import { memo, useState } from 'react';

import {
  AdornedInput,
  Box,
  Divider,
  Form,
  FormField,
  FormHelp,
  IconButton,
  Label,
  Select,
} from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';
import { DescriptionPlaceholder } from '@zg-rentals/zillow-rental-manager';
import { bathOptions, bedOptions, UNIT_NUMBER_LENGTH_LIMIT } from '@zg-rentals/zillow-rental-manager/constants';
import { isUnitNumberOverLimit } from '@zg-rentals/zillow-rental-manager/utils';

type UnitOverviewProps = {
  index: number;
  unitNumber: string;
  bedrooms: string;
  baths: string;
  hasDuplicateUnitNumber?: boolean;
  hasEmptyUnitNumber?: boolean;
  hasMaxCharactersUnitNumber?: boolean;
  hasEmptyBedrooms?: boolean;
  hasEmptyBaths?: boolean;
  disableDeleteButton?: boolean;
  compact?: boolean;
  onChange: (index: string, name: string, value: string) => void;
  onDelete: (index: string) => void;
};

/**
 * Controlled component for unit overview form fields.
 * Validation is handled by the parent component.
 */
const UnitOverview = ({
  onChange,
  onDelete,
  index,
  unitNumber,
  bedrooms,
  baths,
  hasDuplicateUnitNumber = false,
  hasEmptyUnitNumber = false,
  hasMaxCharactersUnitNumber = false,
  hasEmptyBedrooms = false,
  hasEmptyBaths = false,
  disableDeleteButton = false,
  compact = false,
}: UnitOverviewProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const MAX_LENGTH = UNIT_NUMBER_LENGTH_LIMIT;
  const isOverLimit = isUnitNumberOverLimit(unitNumber);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, name } = e.target;
    onChange(index.toString(), name, value);
  };

  const handleDelete = () => {
    onDelete(index.toString());
  };

  return (
    <Box
      data-testid="UnitOverview"
      css={{
        paddingTop: '300',
        paddingBottom: '300',
        paddingLeft: compact ? '0' : '300',
        paddingRight: compact ? '0' : '300',
      }}
    >
      <Form
        data-testid="UnitOverview-Form"
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          gap: { base: '0', lg: '400' },
          justifyContent: 'space-between',
          alignItems: { base: 'stretch', lg: 'flex-start' },
        }}
      >
        <FormField
          control={
            <AdornedInput
              startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
              input={
                <AdornedInput.Input
                  name="unitNumber"
                  value={unitNumber}
                  placeholder={compact ? '1A, 201' : undefined}
                  onChange={handleChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
              }
            />
          }
          label={<Label>Unit number</Label>}
          description={
            hasEmptyUnitNumber ? (
              <FormHelp>Unit number cannot be empty</FormHelp>
            ) : hasDuplicateUnitNumber ? (
              <FormHelp>This unit already exists</FormHelp>
            ) : hasMaxCharactersUnitNumber || isFocused || isOverLimit ? (
              <CharacterCountFormHelp
                currentLength={unitNumber.length}
                maxLength={MAX_LENGTH}
                isFocused={isFocused}
                label="Unit number"
                helperText={compact ? undefined : 'Enter unit number (e.g., 1A, 201)'}
              />
            ) : (
              <DescriptionPlaceholder />
            )
          }
          error={hasEmptyUnitNumber || hasDuplicateUnitNumber || hasMaxCharactersUnitNumber || isOverLimit}
          required={true}
          css={{ flex: 1 }}
        />

        <FormField
          control={
            <Select name="bedrooms" value={bedrooms} onChange={handleChange}>
              {bedOptions.map((option: string) => (
                <option key={option} value={option}>
                  {option || 'Select'}
                </option>
              ))}
            </Select>
          }
          label={<Label>Bedrooms</Label>}
          description={hasEmptyBedrooms ? <FormHelp>Bedrooms cannot be empty</FormHelp> : <DescriptionPlaceholder />}
          error={hasEmptyBedrooms}
          required={true}
          css={{ flex: 1 }}
        />

        <FormField
          control={
            <Select name="baths" value={baths} onChange={handleChange}>
              {bathOptions.map((option: string) => (
                <option key={option} value={option}>
                  {option || 'Select'}
                </option>
              ))}
            </Select>
          }
          label={<Label>Bathrooms</Label>}
          description={hasEmptyBaths ? <FormHelp>Bathrooms cannot be empty</FormHelp> : <DescriptionPlaceholder />}
          error={hasEmptyBaths}
          required={true}
          css={{ flex: 1 }}
        />

        <Box
          data-testid="DeleteButton-Container"
          css={{
            display: 'flex',
            alignItems: { base: 'center', lg: 'flex-start' },
            alignSelf: { base: 'center', lg: 'flex-start' },
            marginBottom: { base: '300', lg: '0' },
            // offset to keep alignment when the form field has an error
            position: 'relative',
            top: { base: '0', lg: '40px' },
          }}
        >
          <IconButton
            tone="neutral"
            emphasis="bare"
            size="md"
            data-testid="DeleteButton"
            title="Delete unit"
            disabled={disableDeleteButton}
            onClick={handleDelete}
          >
            <IconDeleteOutline />
          </IconButton>
        </Box>
      </Form>

      <Divider orientation="horizontal" role="separator" tone="muted" />
    </Box>
  );
};

export default memo(UnitOverview);
